import { registerScope, scopeIdOf, unregisterScope } from '../../pulse/scopeRegistry';
import { createInstanceManager, type InstanceManager } from '../createInstanceManager';
import type { GlobalStateStoreInstance } from './selectors';
import type { GlobalStateStoreInitialValue } from './types';

export type { GlobalStateStoreInstance };

// instanceId is threaded so the per-instance store can derive its own Pulse scopeId.
export type CreateInstanceStoreFactory = (
  instanceId: string,
  initial: GlobalStateStoreInitialValue,
) => GlobalStateStoreInstance;

export type GlobalStateStoreManagerMeta = {
  managerId: string;
  interfacePageId?: string;
};

export type GlobalStateStoreManager = InstanceManager<
  GlobalStateStoreInstance,
  GlobalStateStoreInitialValue
> & {
  /** Pulse manager id, minted in the provider. Read by Pulse instrumentation (e.g. EventHandler). */
  readonly managerId: string;
};

/**
 * Thin adapter over createInstanceManager. The extra setState after createStore is preserved
 * because the base factory accepts `initial` via closure construction but we still need to
 * seed the store state with the per-instance value before it's handed out.
 *
 * Pulse: each per-instance store gets its own scope registered on the real 0→1 acquire and
 * torn down on the real 1→0 dispose (StrictMode synthetic churn is filtered by the manager).
 */
export function createGlobalStateStoreManager(
  baseStore: GlobalStateStoreInstance,
  createStore: CreateInstanceStoreFactory,
  meta: GlobalStateStoreManagerMeta,
): GlobalStateStoreManager {
  const manager = createInstanceManager<GlobalStateStoreInstance, GlobalStateStoreInitialValue>(
    baseStore,
    (instanceId, initial) => {
      const store = createStore(instanceId, initial);
      return store;
    },
    (instanceId) => `Global state store not found for instanceId: ${instanceId}`,
    {
      // thread the per-instance store's getState as the snapshot provider so a stateChange
      // inside a repeatable row snapshots the ROW's store, not base (pm-p04 / pm-014)
      onAcquire: (id, store) =>
        registerScope(
          {
            scopeId: scopeIdOf(meta.managerId, id),
            managerId: meta.managerId,
            instanceId: id,
            interfacePageId: meta.interfacePageId,
            parentScopeId: scopeIdOf(meta.managerId, null),
          },
          () => store.getState(),
        ),
      onDispose: (id) => unregisterScope(scopeIdOf(meta.managerId, id)),
    },
  );

  // Attach managerId without spreading — `baseStore` is a live getter on `manager`
  // and `{...manager}` would collapse it to a stale snapshot (see createInstanceManager).
  return Object.defineProperty(manager, 'managerId', {
    value: meta.managerId,
    enumerable: true,
    writable: false,
  }) as GlobalStateStoreManager;
}
